import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import { supabase } from '@/lib/supabase';
import { BlogPost } from '@/types/blog';

const WHATSAPP_URL = 'https://wa.me/5511989036294?text=Ol%C3%A1%2C%20preciso%20de%20atendimento%20jur%C3%ADdico.';

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [otherPosts, setOtherPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      const { data } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('slug', slug)
        .eq('publicado', true)
        .maybeSingle();
      setPost(data as BlogPost | null);

      if (data) {
        const { data: others } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('publicado', true)
          .neq('slug', slug)
          .order('data_publicacao', { ascending: false })
          .limit(2);
        setOtherPosts((others as BlogPost[]) ?? []);
      }
      setLoading(false);
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <main className="font-sans">
        <Header />
        <div className="min-h-screen bg-[#F8F5EE] flex items-center justify-center pt-20">
          <div className="flex items-center gap-3 text-[#0A1829]/40">
            <i className="ri-loader-4-line animate-spin text-xl"></i>
            <span className="font-sans text-sm">Carregando artigo...</span>
          </div>
        </div>
        <Footer />
      </main>
    );
  }

  if (!post) {
    return (
      <main className="font-sans">
        <Header />
        <div className="min-h-screen bg-[#F8F5EE] flex items-center justify-center pt-20">
          <div className="text-center">
            <h1 className="font-serif text-3xl text-[#0A1829] font-light mb-4">Artigo não encontrado</h1>
            <a href="/blog" onClick={(e) => { e.preventDefault(); navigate('/blog'); }} className="inline-flex items-center gap-2 text-[#B39B62] font-sans font-medium text-sm cursor-pointer">
              <i className="ri-arrow-left-line"></i> Voltar ao Blog
            </a>
          </div>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main className="font-sans">
      <Header />
      <section className="relative pt-32 pb-16 bg-[#0A1829] overflow-hidden">
        {post.imagem_url && (
          <div className="absolute inset-0 opacity-20">
            <img src={post.imagem_url} alt="" className="w-full h-full object-cover object-top" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1829]/80 to-[#0A1829]"></div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 lg:px-12">
          <a href="/blog" onClick={(e) => { e.preventDefault(); navigate('/blog'); }} className="inline-flex items-center gap-2 text-[#B39B62] font-sans text-xs font-semibold tracking-wider uppercase hover:gap-3 transition-all duration-300 cursor-pointer mb-8">
            <i className="ri-arrow-left-line"></i> Blog
          </a>
          <div className="flex items-center gap-3 mb-4">
            <span className="text-[#B39B62] text-xs font-sans font-semibold tracking-[0.2em] uppercase bg-[#B39B62]/10 border border-[#B39B62]/30 px-3 py-1 rounded-full">{post.categoria}</span>
            <span className="text-white/35 text-xs font-sans">{post.tempo_leitura}</span>
          </div>
          <h1 className="font-serif text-3xl lg:text-5xl text-white font-light leading-snug mb-5">{post.titulo}</h1>
          <p className="font-sans text-white/60 text-base leading-relaxed mb-6">{post.resumo}</p>
          <div className="flex items-center gap-4 text-white/35 text-xs font-sans">
            <span>{new Date(post.data_publicacao).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
            <span className="w-1 h-1 rounded-full bg-white/20"></span>
            <span>Moreira Tófoli Advogados</span>
          </div>
        </div>
      </section>

      <article className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="prose-content" dangerouslySetInnerHTML={{ __html: post.conteudo }} style={{ fontFamily: 'inherit', color: 'rgba(10, 24, 41, 0.75)', lineHeight: '1.8', fontSize: '0.9375rem' }} />
          <div className="mt-12 pt-8 border-t border-[#E9D59C]/50">
            <div className="bg-[#F8F5EE] border border-[#E9D59C]/50 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#B39B62]/15 border border-[#B39B62]/30 shrink-0">
                  <i className="ri-scales-3-line text-[#B39B62] text-base"></i>
                </div>
                <div>
                  <h3 className="font-serif text-base text-[#0A1829] font-medium mb-1">Moreira Tófoli Advogados</h3>
                  <p className="font-sans text-[#0A1829]/55 text-xs leading-relaxed">Assessoria jurídica em Direito Penal, Penal Empresarial e Civil. Atuação com rigor técnico e cuidado humano.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <section className="py-16 bg-[#0A1829] text-center">
        <div className="max-w-xl mx-auto px-6">
          <h2 className="font-serif text-2xl lg:text-3xl text-white font-light mb-3">Precisa de <span className="italic text-[#E9D59C]">assessoria jurídica?</span></h2>
          <p className="font-sans text-white/50 text-sm leading-relaxed mb-6">Nosso escritório atende pessoas e empresas em todo o Estado de São Paulo. Fale conosco pelo WhatsApp.</p>
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-[#B39B62] hover:bg-[#E9D59C] text-[#0A1829] font-sans font-semibold text-sm px-8 py-4 rounded-md transition-all duration-300 cursor-pointer whitespace-nowrap">
            <i className="ri-whatsapp-line text-base"></i> Falar com advogado
          </a>
        </div>
      </section>

      {otherPosts.length > 0 && (
        <section className="py-16 bg-[#F8F5EE]">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-8">
              <span className="h-px w-10 bg-[#B39B62]"></span>
              <span className="text-[#B39B62] text-xs font-sans font-semibold tracking-[0.2em] uppercase">Outros artigos</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {otherPosts.map((p) => (
                <div key={p.id} className="bg-white border border-[#E9D59C]/40 rounded-lg p-6 hover:-translate-y-0.5 transition-transform duration-300">
                  <span className="text-[#B39B62] text-xs font-sans font-semibold tracking-widest uppercase block mb-2">{p.categoria}</span>
                  <h3 className="font-serif text-lg text-[#0A1829] font-medium leading-snug mb-2">{p.titulo}</h3>
                  <p className="font-sans text-[#0A1829]/55 text-sm leading-relaxed mb-4 line-clamp-2">{p.resumo}</p>
                  <a href={`/blog/${p.slug}`} onClick={(e) => { e.preventDefault(); navigate(`/blog/${p.slug}`); }} className="inline-flex items-center gap-2 text-[#B39B62] font-sans font-semibold text-xs cursor-pointer hover:gap-3 transition-all duration-300">
                    Ler artigo <i className="ri-arrow-right-line text-xs"></i>
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      <Footer />
    </main>
  );
}
